import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { Switch } from "@mui/material";
import {
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiPlus,
  FiStar,
} from "react-icons/fi";
import {
  createAdminTopupPack,
  getAdminTopupPacks,
  updateAdminTopupPackStatus,
} from "../../api/context/billing";
import { formatRupees } from "../../utils/currency";
import Loader from "../../components/loader/loader";

const CREATE_DEFAULTS = {
  code: "",
  name: "",
  listingCount: "",
  priceInRupees: "",
};

/**
 * The pack with the lowest per-credit price among active packs is the
 * objective "best value" — compute it here instead of letting an admin
 * toggle it manually.
 */
const computeBestValueCode = (packs) => {
  const activePacks = packs.filter((p) => p.isActive && p.listingCount > 0);
  if (!activePacks.length) return null;
  let bestCode = null;
  let bestPerCredit = Infinity;
  activePacks.forEach((p) => {
    const perCredit = p.perListingPriceInPaise ?? Math.round(p.priceInPaise / p.listingCount);
    if (perCredit < bestPerCredit) {
      bestPerCredit = perCredit;
      bestCode = p.code;
    }
  });
  return bestCode;
};

const TopupPacksSection = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [packs, setPacks] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(CREATE_DEFAULTS);
  const [draftErrors, setDraftErrors] = useState({});
  const [confirmDeactivate, setConfirmDeactivate] = useState(null); // pack object
  const [rowBusy, setRowBusy] = useState(null); // code currently patching

  const fetchPacks = () => {
    setLoading(true);
    getAdminTopupPacks()
      .then((res) => {
        if (res?.data?.success) {
          setPacks(res.data.data || []);
        } else {
          toast.error(res?.data?.message || "Failed to load packs");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load packs");
      })
      .finally(() => setLoading(false));
  };

  // Fetch on first expand
  useEffect(() => {
    if (open && packs.length === 0 && !loading) {
      fetchPacks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeCount = packs.filter((p) => p.isActive).length;
  const inactiveCount = packs.length - activeCount;
  const bestValueCode = computeBestValueCode(packs);

  // Sort: active first, then by price ascending (cheapest → premium).
  // Display order is no longer manually managed — price is the single source of truth.
  const sortedPacks = [...packs].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return (a.priceInPaise ?? 0) - (b.priceInPaise ?? 0);
  });

  const validateDraft = () => {
    const errors = {};
    const trimmedCode = (draft.code || "").trim();
    if (!trimmedCode) errors.code = "Code is required";
    else if (!/^[A-Z0-9_]+$/.test(trimmedCode))
      errors.code = "Use uppercase letters, numbers and underscores only";
    if (!(draft.name || "").trim()) errors.name = "Name is required";
    const listingCount = Number(draft.listingCount);
    if (!Number.isInteger(listingCount) || listingCount <= 0)
      errors.listingCount = "Must be a positive integer";
    const priceInRupees = Number(draft.priceInRupees);
    if (!Number.isInteger(priceInRupees) || priceInRupees <= 0)
      errors.priceInRupees = "Must be a positive integer";
    return errors;
  };

  const handleCreate = () => {
    const errors = validateDraft();
    setDraftErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setCreating(true);
    const payload = {
      code: draft.code.trim(),
      name: draft.name.trim(),
      listingCount: Number(draft.listingCount),
      priceInPaise: Number(draft.priceInRupees) * 100,
      currency: "INR",
      isActive: true,
    };
    createAdminTopupPack(payload)
      .then((res) => {
        if (res?.data?.success) {
          toast.success("Pack created");
          setShowCreate(false);
          setDraft(CREATE_DEFAULTS);
          setDraftErrors({});
          fetchPacks();
        } else {
          toast.error(res?.data?.message || "Failed to create pack");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to create pack");
      })
      .finally(() => setCreating(false));
  };

  const patchStatus = (code, patch, successMsg) => {
    setRowBusy(code);
    updateAdminTopupPackStatus(code, patch)
      .then((res) => {
        if (res?.data?.success) {
          if (successMsg) toast.success(successMsg);
          const updated = res.data.data;
          setPacks((prev) => prev.map((p) => (p.code === code ? updated : p)));
        } else {
          toast.error(res?.data?.message || "Update failed");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Update failed");
      })
      .finally(() => setRowBusy(null));
  };

  const handleToggleActive = (pack) => {
    if (pack.isActive) {
      // Needs confirm before deactivating
      setConfirmDeactivate(pack);
    } else {
      patchStatus(pack.code, { isActive: true }, "Pack activated");
    }
  };

  const confirmDeactivation = () => {
    if (!confirmDeactivate) return;
    const code = confirmDeactivate.code;
    setConfirmDeactivate(null);
    patchStatus(code, { isActive: false }, "Pack deactivated");
  };

  return (
    <div className="adm-card topup-packs-card">
      <style>{`
        .topup-packs-card .adm-section-head {
          cursor: pointer;
        }
        .topup-packs-card .adm-section-head-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .topup-packs-card .adm-summary-chip {
          background: #ecfdf5;
          color: #047857;
          border: 1px solid #a7f3d0;
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 999px;
        }
        .topup-packs-card .adm-summary-chip.is-muted {
          background: #f1f5f9;
          color: #64748b;
          border-color: #e2e8f0;
        }

        .topup-packs-card .adm-banner {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          color: #075985;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 12px;
          line-height: 1.45;
          display: flex;
          gap: 10px;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .topup-packs-card .adm-banner svg {
          flex-shrink: 0;
          margin-top: 2px;
          color: #0284c7;
        }

        .topup-packs-card .admin-topup-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 14px;
        }
        @media (max-width: 575.98px) {
          .topup-packs-card .admin-topup-grid { grid-template-columns: 1fr; gap: 12px; }
        }

        /* Card shell — single 2-column grid shared by top + bottom rows
           so left/right items in each row land in the same columns. */
        .topup-packs-card .admin-topup-card {
          position: relative;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr auto;
          grid-auto-rows: auto;
          row-gap: 22px;
          column-gap: 12px;
          padding: 18px 16px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
        }
        .topup-packs-card .admin-topup-card:hover {
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
          border-color: #cbd5e1;
          transform: translateY(-1px);
        }
        .topup-packs-card .admin-topup-card.is-best {
          border-color: rgba(0, 134, 149, 0.4);
          box-shadow: 0 0 0 2px rgba(0, 134, 149, 0.08), 0 2px 10px rgba(0, 134, 149, 0.05);
        }
        .topup-packs-card .admin-topup-card.is-inactive {
          background: #fafbfd;
          border-color: #e5e7eb;
          box-shadow: none;
        }
        .topup-packs-card .admin-topup-card.is-inactive .admin-topup-hero,
        .topup-packs-card .admin-topup-card.is-inactive .admin-topup-head-text {
          opacity: 0.65;
        }

        /* Head text (col 1 of row 1) */
        .topup-packs-card .admin-topup-head-text {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-self: center;
          grid-column: 1;
          grid-row: 1;
        }
        .topup-packs-card .admin-topup-title-row {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
        }
        .topup-packs-card .admin-topup-name {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          line-height: 1.25;
          letter-spacing: -0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }
        .topup-packs-card .admin-topup-name-star {
          color: #f59e0b;
          flex-shrink: 0;
          display: inline-block;
          line-height: 0;
        }
        .topup-packs-card .admin-topup-code {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 10px;
          color: #64748b;
          letter-spacing: 0.04em;
          line-height: 1.1;
          text-transform: none;
        }

        /* Top-right active toggle (col 2 of row 1) */
        .topup-packs-card .admin-topup-head-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          flex-shrink: 0;
          grid-column: 2;
          grid-row: 1;
          align-self: center;
        }
        .topup-packs-card .admin-topup-head-toggle .MuiSwitch-root {
          margin: -6px -8px -6px 0;
        }
        .topup-packs-card .admin-topup-head-busy {
          color: #008695;
          margin-right: 4px;
        }

        /* Price (col 1 of row 2) */
        .topup-packs-card .admin-topup-price {
          font-size: 26px;
          font-weight: 800;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -0.025em;
          font-variant-numeric: tabular-nums;
          grid-column: 1;
          grid-row: 2;
          align-self: center;
        }

        /* Credits meta (col 2 of row 2) */
        .topup-packs-card .admin-topup-hero-meta {
          text-align: right;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
          grid-column: 2;
          grid-row: 2;
          align-self: center;
        }
        .topup-packs-card .admin-topup-hero-meta strong {
          font-size: 12.5px;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.2;
          font-variant-numeric: tabular-nums;
        }
        .topup-packs-card .admin-topup-hero-meta small {
          font-size: 10.5px;
          color: #94a3b8;
          font-weight: 500;
          line-height: 1.2;
          font-variant-numeric: tabular-nums;
        }



        .topup-packs-card .adm-chevron {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          color: #64748b;
          background: transparent;
          border: none;
          cursor: pointer;
        }
        .topup-packs-card .adm-chevron:hover { background: #e2e8f0; color: #0f172a; }

        .topup-packs-create-modal .form-label {
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #475569 !important;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          margin-bottom: 4px !important;
        }
        .topup-packs-create-modal .form-control,
        .topup-packs-create-modal .form-select {
          font-size: 12.5px !important;
          height: 36px;
        }
        .topup-packs-create-modal textarea.form-control { height: auto; }
        .topup-packs-create-modal .form-error {
          color: #dc2626;
          font-size: 11px;
          margin-top: 3px;
        }
      `}</style>

      <div
        className="adm-section-head"
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div className="adm-section-head-text">
          <h5 className="adm-section-title">AI-Listing Top-up Packs</h5>
          <p className="adm-section-sub">
            Manage packs vendors can buy from the storefront.
          </p>
        </div>
        <div className="adm-section-head-right">
          {!loading && packs.length > 0 && (
            <>
              <span className="adm-summary-chip">{activeCount} active</span>
              {inactiveCount > 0 && (
                <span className="adm-summary-chip is-muted">
                  {inactiveCount} inactive
                </span>
              )}
            </>
          )}
          {open && (
            <button
              type="button"
              className="adm-btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                setDraft(CREATE_DEFAULTS);
                setDraftErrors({});
                setShowCreate(true);
              }}
            >
              <FiPlus size={13} strokeWidth={2.4} />
              <span>New<span className="d-none d-sm-inline"> pack</span></span>
            </button>
          )}
          <span className="adm-chevron" aria-hidden="true">
            {open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </span>
        </div>
      </div>

      {open && (
        <div className="adm-card-body">
          <div className="adm-banner">
            <FiInfo size={14} />
            <div>
              <strong>Packs cannot be price-edited after creation.</strong>{" "}
              To change a price, create a new pack and deactivate the old one.
              Deactivated packs disappear from the vendor-facing storefront but
              stay in the DB so transaction history remains intact.
            </div>
          </div>

          {loading ? (
            <div className="text-center py-3">
              <Loader />
            </div>
          ) : packs.length === 0 ? (
            <div className="text-center py-4 text-muted" style={{ fontSize: 12 }}>
              No packs yet. Click <strong>New pack</strong> to create your first one.
            </div>
          ) : (
            <div className="admin-topup-grid">
              {sortedPacks.map((pack) => {
                const perListing = pack.perListingPriceInPaise ??
                  (pack.listingCount > 0
                    ? Math.round(pack.priceInPaise / pack.listingCount)
                    : null);
                const isBusy = rowBusy === pack.code;
                const isBest = pack.code === bestValueCode;
                return (
                  <div
                    key={pack.code}
                    className={[
                      "admin-topup-card",
                      isBest ? "is-best" : "",
                      pack.isActive ? "" : "is-inactive",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="admin-topup-head-text">
                      <div className="admin-topup-title-row">
                        <h6 className="admin-topup-name">{pack.name}</h6>
                        {isBest && (
                          <FiStar
                            className="admin-topup-name-star"
                            size={13}
                            strokeWidth={2.4}
                            fill="currentColor"
                            aria-label="Best value"
                          />
                        )}
                      </div>
                      <span className="admin-topup-code">{pack.code}</span>
                    </div>
                    <div className="admin-topup-head-toggle">
                      {isBusy ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="admin-topup-head-busy"
                        />
                      ) : (
                        <Switch
                          size="small"
                          checked={!!pack.isActive}
                          disabled={isBusy}
                          onChange={() => handleToggleActive(pack)}
                          inputProps={{ "aria-label": "Toggle active" }}
                        />
                      )}
                    </div>

                    <div className="admin-topup-price">
                      {formatRupees(pack.priceInPaise)}
                    </div>
                    <div className="admin-topup-hero-meta">
                      <strong>
                        {pack.listingCount} credit{pack.listingCount === 1 ? "" : "s"}
                      </strong>
                      {perListing != null && (
                        <small>{formatRupees(perListing)} / credit</small>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create pack modal */}
      <Modal
        show={showCreate}
        onHide={() => !creating && setShowCreate(false)}
        centered
        className="topup-packs-create-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>
            Create new top-up pack
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="gx-2">
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Code *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. TOPUP_50_V2"
                  value={draft.code}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, code: e.target.value.toUpperCase() }))
                  }
                  isInvalid={!!draftErrors.code}
                />
                {draftErrors.code && (
                  <div className="form-error">{draftErrors.code}</div>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="50 Listings Pack"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, name: e.target.value }))
                  }
                  isInvalid={!!draftErrors.name}
                />
                {draftErrors.name && (
                  <div className="form-error">{draftErrors.name}</div>
                )}
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>Listing count *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  placeholder="50"
                  value={draft.listingCount}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, listingCount: e.target.value }))
                  }
                  isInvalid={!!draftErrors.listingCount}
                />
                {draftErrors.listingCount && (
                  <div className="form-error">{draftErrors.listingCount}</div>
                )}
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>Price (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  placeholder="449"
                  value={draft.priceInRupees}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, priceInRupees: e.target.value }))
                  }
                  isInvalid={!!draftErrors.priceInRupees}
                />
                {draftErrors.priceInRupees && (
                  <div className="form-error">{draftErrors.priceInRupees}</div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setShowCreate(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            style={{ background: "#008695", border: "1px solid #008695" }}
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? (
              <>
                <Spinner animation="border" size="sm" /> Creating...
              </>
            ) : (
              "Create pack"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deactivation confirm */}
      <Modal
        show={!!confirmDeactivate}
        onHide={() => setConfirmDeactivate(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>
            Deactivate pack?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13, color: "#334155" }}>
          Vendors will stop seeing <strong>{confirmDeactivate?.name}</strong>{" "}
          (<code style={{ fontSize: 11 }}>{confirmDeactivate?.code}</code>) immediately.
          Existing transactions are unaffected. Continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setConfirmDeactivate(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeactivation}
          >
            Deactivate
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TopupPacksSection;
